import { useEffect, useMemo, useState } from 'react'
import {
  MRT_PaginationState,
  type MRT_ColumnDef,
  MRT_SortingState,
  MRT_ColumnFiltersState,
} from 'mantine-react-table'
import { findLabelByValue, formatDateForTable } from '@/shared/lib/utils'
import { useTranslation } from 'react-i18next'
import { useLanguageStore } from '@/widgets/LanguageSelect/model/store'
import { Badge } from '@mantine/core'
import { useAgentActionsByPage } from '@/entities/PlatformActivity/api/agent-actions-api'
import { useTranslatedEnums } from '@/shared/helpers/apiEnumTypesOptions'
import { generateLink } from '@/widgets/DashboardView/lib/const'
import { UserActionsLogListItem } from '@/shared/api/topli/TopliApi'
import { eventTypeColors } from '../../EventsTable/lib/consts'
import { useTableFilters } from '@/shared/hooks/useTableFilters'

export const useUserActionsTable = (userId: number) => {
  const [search] = useState('')
  const [sorting, setSorting] = useState<MRT_SortingState>([])
  const { agentActionsOptions } = useTranslatedEnums()
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  })
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
  const { getDateRange, getMultiSelectIds } = useTableFilters(columnFilters)

  const memoizedSorting = useMemo(() => {
    let orderBy = ''
    if (!sorting.length) return null
    sorting.forEach(item => (item.desc ? (orderBy = `-${item.id}`) : (orderBy = `${item.id}`)))
    return orderBy
  }, [sorting])

  const selectedActionIds = getMultiSelectIds('action')

  const { t } = useTranslation(['dashboard'])
  const { language } = useLanguageStore()

  const {
    data: userActions,
    isLoading: isLoadingUserActions,
    isError: isErrorUserActions,
    isFetching: isFetchingUserActions,
  } = useAgentActionsByPage(
    memoizedSorting,
    pagination.pageSize,
    pagination.pageIndex * pagination.pageSize,
    search,
    selectedActionIds,
    userId,
  )

  const fetchedUserActions = userActions?.data ?? []
  const totalRowCount = userActions?.total ?? 0

  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }))
  }, [columnFilters])

  const columns = useMemo<MRT_ColumnDef<UserActionsLogListItem>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        enableColumnFilter: false,
        size: 90,
      },
      {
        accessorKey: 'created_at',
        header: t('dashboard:agent_actions.created_at'),
        enableColumnFilter: false,
        Cell: ({ row }) => formatDateForTable(row.original.created_at, language),
      },
      {
        accessorKey: 'action',
        filterFn: 'arrIncludesSome',
        filterVariant: 'multi-select',
        mantineFilterMultiSelectProps: {
          data: agentActionsOptions.map(opt => ({
            label: opt.label,
            value: opt.value,
          })),
        },
        header: t('dashboard:agent_actions.action'),
        Cell: ({ row }) => {
          return row.original.action ? (
            <Badge size='sm' color={eventTypeColors[row.original.action]}>
              {findLabelByValue(agentActionsOptions, row.original.action)}
            </Badge>
          ) : (
            '–'
          )
        },
      },
      {
        accessorKey: 'link',
        enableSorting: false,
        header: t('dashboard:agent_actions.link'),
        enableColumnFilter: false,
        Cell: ({ row }) => generateLink(row.original, t),
      },
    ],
    [language],
  )

  return {
    pagination,
    fetchedUserActions,
    totalRowCount,
    columns,
    isErrorUserActions,
    isFetchingUserActions,
    isLoadingUserActions,
    sorting,
    setSorting,
    setPagination,
    columnFilters,
    setColumnFilters,
  }
}
